import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Datastore, DatastoreCollection } from '@freelancer/datastore';
import {
  CountriesCollection,
  Currency,
  UserDepositMethod,
  UserDepositMethodsCollection,
  UsersSelf,
} from '@freelancer/datastore/collections';
import {
  PaymentsErrorType,
  PaymentsMessagingService,
  PaymentsResultStatus,
} from '@freelancer/payments-messaging';
import { PaymentsTrackingService } from '@freelancer/payments-tracking';
import {
  methodSelectionRadioControl,
  PaymentsUtils,
} from '@freelancer/payments-utils';
import { CardBorderRadius } from '@freelancer/ui/card';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ListItemPadding } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import { TextSize } from '@freelancer/ui/text';
import { UserAgent } from '@freelancer/user-agent';
import { fromPairs, isDefined } from '@freelancer/utils';
import { DepositMethodApi } from 'api-typings/payments/payments';
import * as Rx from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { CreditCardMethodComponent } from '../../../common/credit-card-method/credit-card-method.component';
import { SourceDisplayMode } from '../../../common/source-template/source-template.component';
import { DepositData, GenericPayload } from '../../../common/types';
import {
  mapValueToSelectedCurrencies,
  MethodPaymentCurrency,
  MethodSelectedCurrencies,
} from '../payments.helpers';
import { BankDepositMethodComponent } from './bank-deposit-method/bank-deposit-method.component';
import { BoletoMethodComponent } from './boleto-method/boleto-method.component';
import { availableDepositMethods } from './methods.types';

export interface DepositMethodMap {
  [id: string]: Rx.Observable<UserDepositMethod>;
}

export interface DepositDataMap {
  [id: string]: Rx.Observable<DepositData>;
}

type PaymentMethodComponent =
  | CreditCardMethodComponent
  | BankDepositMethodComponent
  | BoletoMethodComponent;

@Component({
  selector: 'app-methods',
  template: `
    <ng-container *ngFor="let depositMethod of visibleMethods$ | flAsync">
      <ng-container [ngSwitch]="depositMethod.id">
        <!-- TODO - update to use enums after T52455 -->
        <app-credit-card-method
          #paymentMethod
          *ngSwitchCase="DepositMethodApi.FLN_BILLING"
          [countries$]="countriesCollection.valueChanges()"
          [currencies]="currencies$ | flAsync"
          [depositData]="depositDataMap[depositMethod.id] | flAsync"
          [depositFormGroup]="depositFormGroup"
          [genericPayload]="genericPayload"
          [group]="group"
          [id]="depositMethod.id"
          [popUpEnabled]="popUpEnabled"
          [userInfo$]="userInfo$"
          [cartId]="cartId"
          [sourceDisplayMode]="sourceDisplayMode"
        ></app-credit-card-method>

        <app-bank-deposit-method
          #paymentMethod
          *ngSwitchCase="DepositMethodApi.WIRE"
          [countries$]="countriesCollection.valueChanges()"
          [currencies$]="currencies$"
          [depositData]="depositDataMap[depositMethod.id] | flAsync"
          [genericPayload]="genericPayload"
          [depositFormGroup]="depositFormGroup"
          [group]="group"
          [id]="depositMethod.id"
          [userInfo$]="userInfo$"
          [sourceDisplayMode]="sourceDisplayMode"
        ></app-bank-deposit-method>

        <app-boleto-method
          #paymentMethod
          *ngSwitchCase="DepositMethodApi.BOLETO"
          [countries$]="countriesCollection.valueChanges()"
          [depositData]="depositDataMap[depositMethod.id] | flAsync"
          [depositFormGroup]="depositFormGroup"
          [genericPayload]="genericPayload"
          [group]="group"
          [id]="depositMethod.id"
          [depositMethod$]="depositMethodMap[depositMethod.id]"
          [userInfo$]="userInfo$"
          [sourceDisplayMode]="sourceDisplayMode"
        ></app-boleto-method>

        <app-ideal-method
          #paymentMethod
          *ngSwitchCase="DepositMethodApi.IDEAL"
          [depositData]="depositDataMap[depositMethod.id] | flAsync"
          [depositFormGroup]="depositFormGroup"
          [genericPayload]="genericPayload"
          [backUrlEncoded]="backUrlEncoded"
          [group]="group"
          [id]="depositMethod.id"
          [userInfo$]="userInfo$"
          [sourceDisplayMode]="sourceDisplayMode"
        ></app-ideal-method>

        <app-webmoney-method
          #paymentMethod
          *ngSwitchCase="DepositMethodApi.WEB_MONEY"
          [depositData]="depositDataMap[depositMethod.id] | flAsync"
          [depositFormGroup]="depositFormGroup"
          [genericPayload]="genericPayload"
          [group]="group"
          [id]="depositMethod.id"
          [userInfo$]="userInfo$"
          [sourceDisplayMode]="sourceDisplayMode"
        ></app-webmoney-method>

        <ng-container *ngFor="let method of methods">
          <app-generic-method
            #paymentMethod
            *ngSwitchCase="method.id"
            [currencies]="currencies$ | flAsync"
            [depositData]="depositDataMap[depositMethod.id] | flAsync"
            [depositFormGroup]="depositFormGroup"
            [depositMethodId]="method.id"
            [genericPayload]="genericPayload"
            [backUrlEncoded]="backUrlEncoded"
            [group]="group"
            [id]="depositMethod.id"
            [logoReplacementText]="method.logoReplacementText"
            [forceDefaultCurrencyAsPaymentCurrency]="
              method.forceDefaultCurrencyAsPaymentCurrency
            "
            [depositMethod$]="depositMethodMap[depositMethod.id]"
            [enablePopUp]="method.enablePopUp"
            [label]="method.label"
            [sourceDisplayMode]="sourceDisplayMode"
          ></app-generic-method>
        </ng-container>
      </ng-container>
    </ng-container>
    <ng-container>
      <fl-list-item
        *ngIf="showExpandMethods$ | flAsync"
        flTrackingLabel="showAllMethodListItem"
        [padding]="ListItemPadding.MID"
        [selectable]="true"
        [clickable]="true"
        (click)="expandMethodList()"
      >
        <fl-bit class="ShowAllItem">
          <fl-text
            i18n="Payment methods list - show more methods"
            [size]="TextSize.SMALL"
          >
            Show All Payment Options
          </fl-text>
          <fl-text
            class="RedirectionCopy"
            *ngIf="instantPaymentOnly$ | flAsync; else chevron"
            i18n="Payment methods list - redirection info"
            [size]="TextSize.XXSMALL"
          >
            You will be redirected to another page.
          </fl-text>
          <ng-template #chevron>
            <fl-icon
              [name]="'ui-chevron-down'"
              [color]="IconColor.PRIMARY"
              [size]="IconSize.SMALL"
              [flMarginRight]="Margin.XSMALL"
            ></fl-icon>
          </ng-template>
        </fl-bit>
      </fl-list-item>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./methods.component.scss'],
})
export class MethodsComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  CardBorderRadius = CardBorderRadius;
  DepositMethodApi = DepositMethodApi;
  TextSize = TextSize;
  IconColor = IconColor;
  IconSize = IconSize;
  Margin = Margin;
  SourceDisplayMode = SourceDisplayMode;

  private subscriptions: readonly Rx.Subscription[] = [];
  initialQueryList: QueryList<PaymentMethodComponent>;
  genericPayload: GenericPayload;
  popUpEnabled: boolean;
  methodListExpanded = false;
  countriesCollection: DatastoreCollection<CountriesCollection>;
  visibleMethods$: Rx.Observable<readonly UserDepositMethod[]>;
  ListItemPadding = ListItemPadding;
  methods = availableDepositMethods;
  private expandMethodsSubject$ = new Rx.BehaviorSubject<boolean>(false);
  expandMethods$ = this.expandMethodsSubject$.asObservable();
  showExpandMethods$: Rx.Observable<boolean>;

  readonly methodRenderingThreshold = 5;

  @Input() backUrl?: string;
  @Input() backUrlEncoded?: string;
  @Input() isSmallScreen: boolean;
  @Input() instantPaymentOnly$: Rx.Observable<boolean>;
  @Input() currencies$: Rx.Observable<readonly Currency[]>;
  @Input() depositDataMap: DepositDataMap;
  @Input() depositFormGroup: FormGroup;
  @Input()
  depositMethodsCollection: DatastoreCollection<UserDepositMethodsCollection>;
  @Input() depositMethodMap: DepositMethodMap;
  @Input() group: FormGroup;
  @Input() threatMetrixSession: string;
  @Input() trackingToken?: string;
  @Input() userId: number;
  @Input() userInfo$: Rx.Observable<UsersSelf>;
  @Input() cartId?: number;
  @Input() sourceDisplayMode: SourceDisplayMode;
  @Input() isHourlyInitialPayment$: Rx.Observable<boolean>;

  @Output()
  methodPaymentCurrencySelected = new EventEmitter<MethodPaymentCurrency>();

  @ViewChildren('paymentMethod')
  set setPaymentMethod(queryList: QueryList<PaymentMethodComponent>) {
    if (this.initialQueryList) {
      return;
    }
    this.initialQueryList = queryList;
  }

  @ViewChildren('paymentMethod')
  paymentMethodsQueryList: QueryList<PaymentMethodComponent>;

  constructor(
    private datastore: Datastore,
    private paymentsMessaging: PaymentsMessagingService,
    private paymentsTracking: PaymentsTrackingService,
    private userAgent: UserAgent,
    private paymentsUtils: PaymentsUtils,
  ) {}

  ngOnInit() {
    this.popUpEnabled = !this.userAgent.isBrowserIE();
    this.countriesCollection = this.datastore.collection<CountriesCollection>(
      'countries',
    );
    this.subscriptions = [
      ...this.subscriptions,
      this.countriesCollection.status$.subscribe(status => {
        if (status.error) {
          this.paymentsMessaging.pushError({
            errorType: PaymentsErrorType.ERROR_DATASTORE,
            errorCode: status.error.errorCode,
            retry: status.error.retry,
          });
        }
      }),
    ];
    if (!this.popUpEnabled) {
      this.methods.map(m => {
        m.enablePopUp = false;
      });
    }

    this.visibleMethods$ = Rx.combineLatest([
      this.depositMethodsCollection.valueChanges(),
      this.expandMethods$,
      this.instantPaymentOnly$,
      this.isHourlyInitialPayment$,
    ]).pipe(
      map(
        ([
          depositMethods,
          expandMethods,
          instantPaymentOnly,
          isHourlyInitialPayment,
        ]) => {
          // If instant payment is required, the methods visibility
          // depends on their instantPayment field
          if (instantPaymentOnly) {
            return depositMethods.filter(
              depositMethod => depositMethod.instantPayment,
            );
          }

          if (isHourlyInitialPayment) {
            return depositMethods.filter(method =>
              [DepositMethodApi.FLN_BILLING].includes(
                method.id as DepositMethodApi,
              ),
            );
          }

          if (this.isSmallScreen || expandMethods) {
            return depositMethods;
          }

          return depositMethods.slice(0, this.methodRenderingThreshold);
        },
      ),
    );

    this.showExpandMethods$ = Rx.combineLatest([
      this.expandMethods$,
      this.instantPaymentOnly$,
      this.isHourlyInitialPayment$,
    ]).pipe(
      map(
        ([expandMethods, instantPaymentOnly, isHourlyInitialPayment]) =>
          instantPaymentOnly ||
          (!expandMethods && !this.isSmallScreen && !isHourlyInitialPayment),
      ),
    );

    const redirectToDepositPageForMoreMethods$ = Rx.combineLatest([
      this.instantPaymentOnly$,
      this.expandMethods$,
    ]).pipe(
      tap(([instantPaymentOnly, expendMethods]) => {
        if (instantPaymentOnly && expendMethods) {
          this.paymentsMessaging.pushResult({
            paymentsStatus: PaymentsResultStatus.REDIRECT,
          });
        }
      }),
    );

    this.subscriptions = [
      ...this.subscriptions,
      redirectToDepositPageForMoreMethods$.subscribe(),
    ];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (!this.genericPayload ||
        'backUrl' in changes ||
        'cartId' in changes ||
        'userId' in changes ||
        'trackingToken' in changes ||
        'threatMetrixSession' in changes) &&
      this.userId
    ) {
      this.genericPayload = {
        user_id: this.userId,
        cancel_url: this.paymentsUtils.getCancelUrl(this.popUpEnabled),
        // may or may not be defined
        threatmetrix_session: this.threatMetrixSession,
        // may or may not be defined
        tracking_token: this.trackingToken,
        // may or may not be defined
        back_url: this.backUrl,
        country_code: '',
        action: '',
        domain: '',
        cart_id: this.cartId,
      };
    }
  }

  ngAfterViewInit() {
    // Due to some angular nuance we miss the first `changes` emission
    // ( likely to do with a surrounding *ngIf )
    // To account for this, we catch the first emission in the
    // `set` function above and manually startWith it.
    const methodSelectedCurrencies$: Rx.Observable<MethodSelectedCurrencies> = this.paymentMethodsQueryList.changes.pipe(
      startWith<QueryList<PaymentMethodComponent>>(this.initialQueryList),
      map(queryList =>
        fromPairs(
          queryList
            .map(el =>
              el.paymentCurrencySelected
                ? ([el.id, el.paymentCurrencySelected.asObservable()] as const)
                : undefined,
            )
            .filter(isDefined),
        ),
      ),
    );

    const control = this.group.get(methodSelectionRadioControl);
    if (control) {
      const currency$ = mapValueToSelectedCurrencies(
        control,
        methodSelectedCurrencies$,
      );
      this.subscriptions = [
        ...this.subscriptions,
        currency$.subscribe(v => {
          this.methodPaymentCurrencySelected.emit(v);
        }),
      ];
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  expandMethodList() {
    this.paymentsTracking.pushTrackingData({
      label: 'expandMorePaymentMethods',
    });

    this.expandMethodsSubject$.next(true);
  }
}
